import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Input,
  InputRef,
  List,
  message,
  Row,
  Space,
  Table,
  TableColumnsType,
  Typography
} from 'antd';
import { DownloadOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons';
import moment from 'moment';
import { UserRecord } from 'sb/models/User';
import { DEFAULT_TABLE_PAGINATION_CONFIG } from 'sb/shared/constants';
import { filter, isNil } from 'lodash-es';
import fileDownload from 'js-file-download';
import { ExcelCreateRequest, FieldType, Horizontal, NumberFormat, Vertical } from 'sb/models/ExcelJs';
import 'components/molecules/admin/Users.scss';
import { useExtraApi } from 'sb/api/extra';
import { useExcelApi } from 'sb/api/excel';
import { FieldSorter } from '../../shared/Helpers';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';

const Users: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [UserOrig, setUserOrig] = useState<UserRecord[]>([]);
  const [creatingExcel, setCreatingExcel] = useState(false);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);

  const filterInputRef = useRef<InputRef>(null);

  // API
  const { getUsers } = useExtraApi();
  const { createExcel } = useExcelApi();

  useEffect(() => {
    handleClickRefresh().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickRefresh = async () => {
    setLoading(true);
    const res = await getUsers();
    if (Array.isArray(res)) {
      const normalized = res.map((item: UserRecord) => {
        item.logins_count = item.logins_count || 0;
        return item;
      });
      setUserOrig(normalized);
      // Default view: Active users only
      setUsers(normalized.filter((user) => !user.blocked));
      setShowInactiveUsers(false);
      setTimeout(() => {
        filterInputRef.current?.focus();
      }, 1000);
    }
    setLoading(false);
  };

  const handleExport = async () => {
    const filename = `NPI-Digital-Users ${moment().format('YYYY-MM-DD hh-mm a')}.xlsx`;

    const formData: ExcelCreateRequest = {
      data: users.map((user: UserRecord) => {
        return {
          first_name: user.first_name,
          last_name: user.last_name,
          title: user.title,
          email: user.email,
          blocked: !user.blocked ? 'Active' : 'Inactive',
          salesforce_id: user?.client?.salesforce_id,
          parent: user?.client?.parent,
          grand_parent: user?.client?.grand_parent,
          sso: user?.client?.sso,
          name: user?.client?.name,
          logins_count: user.logins_count,
          last_login:
            user.auth_log.length > 0 ? moment(user.auth_log[0].login_date).format('MM/DD/YYYY hh:mm A') : null,
          created_date: user.created_date,
          roles: user.user_roles?.join(', ')
        };
      }),
      options: { fileName: filename, workSheetName: 'Users' },
      columns: [
        { header: 'First Name', key: 'first_name', width: 16, type: FieldType.String },
        { header: 'Last Name', key: 'last_name', width: 16, type: FieldType.String },
        { header: 'Account Name', key: 'name', width: 32, type: FieldType.String },
        { header: 'Status', key: 'blocked', width: 16, type: FieldType.String },
        { header: 'Title', key: 'title', width: 32, type: FieldType.String },
        { header: 'Parent', key: 'parent', width: 22, type: FieldType.String },
        { header: 'Grandparent', key: 'grand_parent', width: 22, type: FieldType.String },
        { header: 'SSO', key: 'sso', width: 32, type: FieldType.String },
        { header: 'Email', key: 'email', width: 32, type: FieldType.String },
        { header: 'Salesforce ID', key: 'salesforce_id', width: 30, type: FieldType.String },
        { header: 'Date Created', key: 'created_date', width: 20, type: FieldType.Date, numFmt: 'mm/dd/yyyy' },
        {
          header: 'Logins',
          key: 'logins_count',
          width: 8,
          type: FieldType.Number,
          alignment: {
            horizontal: Horizontal.center,
            vertical: Vertical.bottom,
            wrapText: false
          },
          numFmt: NumberFormat.integer
        },
        { header: 'Last Login', key: 'last_login', width: 20, type: FieldType.String },
        {
          header: 'Roles',
          key: 'roles',
          width: 64,
          type: FieldType.String,
          alignment: {
            horizontal: Horizontal.left,
            vertical: Vertical.bottom,
            wrapText: true
          }
        }
      ]
    };

    try {
      setCreatingExcel(true);
      const excelData = await createExcel(formData);
      fileDownload(excelData, filename);
    } catch (e) {
      message.error('Problem creating Excel file.');
    } finally {
      setCreatingExcel(false);
    }
  };

  const columns: TableColumnsType<UserRecord> = useMemo(
    () => [
      {
        title: 'First Name',
        dataIndex: 'first_  name',
        sortDirections: ['ascend', 'descend'],
        sorter: (a: UserRecord, b: UserRecord) => FieldSorter(a.first_name, b.first_name),
        render: (_, rec: UserRecord) => `${rec.first_name || '--'}`
      },
      {
        title: 'Last Name',
        dataIndex: 'last_name',
        sortDirections: ['ascend', 'descend'],
        sorter: (a: UserRecord, b: UserRecord) => FieldSorter(a.first_name, b.first_name),
        render: (_, rec: UserRecord) => ` ${rec.last_name || ' '}`
      },
      {
        title: 'Account Name',
        dataIndex: 'client.name',
        sortDirections: ['ascend', 'descend'],
        render: (_, rec: UserRecord) => `${rec.client?.name || '--'}`,
        sorter: (a: UserRecord, b: UserRecord) => FieldSorter(a.client?.name, b.client?.name)
      },
      {
        title: 'Status',
        dataIndex: 'blocked',
        sortDirections: ['ascend', 'descend'],
        render: (_, rec: UserRecord) => `${!rec.blocked ? 'Active' : 'Inactive'}`,
        sorter: (a: UserRecord, b: UserRecord) =>
          FieldSorter(a.blocked ? 'Inactive' : 'Active', b.blocked ? 'Inactive' : 'Active')
      },
      {
        title: 'Title',
        dataIndex: 'title',
        render: (_, rec: UserRecord) => rec.title || '--',
        sortDirections: ['ascend', 'descend'],
        sorter: (a: UserRecord, b: UserRecord) => FieldSorter(a.title, b.title)
      },
      {
        title: 'Parent',
        dataIndex: 'client.parent',
        sortDirections: ['ascend', 'descend'],
        render: (_, rec: UserRecord) => `${rec.client?.parent || ' - '}`,
        sorter: (a: UserRecord, b: UserRecord) => FieldSorter(a.client?.parent, b.client?.parent)
      },
      {
        title: 'Grandparent',
        dataIndex: 'client.grand_parent',
        sortDirections: ['ascend', 'descend'],
        render: (_, rec: UserRecord) => `${rec.client?.grand_parent || ' - '}`,
        sorter: (a: UserRecord, b: UserRecord) => FieldSorter(a.client?.grand_parent, b.client?.grand_parent)
      },
      {
        title: 'SSO',
        dataIndex: 'sso',
        sortDirections: ['ascend', 'descend'],
        render: (_, rec: UserRecord) => `${rec.client?.sso || ' - '}`
      },
      {
        title: 'Email',
        dataIndex: 'email',
        sortDirections: ['ascend', 'descend'],
        sorter: (a: UserRecord, b: UserRecord) => FieldSorter(a.email, b.email)
      },
      {
        title: 'Roles',
        dataIndex: 'user_roles',
        render: (_, rec: UserRecord) => {
          if (rec.user_roles && rec.user_roles?.length > 0) {
            return (
              <List
                size="small"
                bordered={false}
                split={false}
                dataSource={rec.user_roles}
                renderItem={(role: any) => (
                  <List.Item>
                    <div className="role-label">{role}</div>
                  </List.Item>
                )}
              />
            );
          }
          return '--';
        }
      },
      {
        title: 'Date Created',
        dataIndex: 'created_date',
        sortDirections: ['ascend', 'descend'],
        sorter: (a: UserRecord, b: UserRecord) => FieldSorter(a.created_date, b.created_date),
        render: (_, rec: UserRecord) => moment(rec.created_date).format('ll')
      },
      {
        title: 'Logins',
        dataIndex: 'logins_count',
        align: 'center',
        sortDirections: ['descend', 'ascend'],
        sorter: (a: UserRecord, b: UserRecord) => a.logins_count - b.logins_count
      },
      {
        title: 'Last Login',
        dataIndex: 'last_login',
        sortDirections: ['descend', 'ascend'],
        sorter: (a: UserRecord, b: UserRecord) => {
          const loginA = a.auth_log.length > 0 ? a.auth_log[0].login_date : null;
          const loginB = b.auth_log.length > 0 ? b.auth_log[0].login_date : null;
          return FieldSorter(loginA, loginB);
        },
        render: (_, rec: UserRecord) =>
          rec.auth_log.length > 0 ? moment(rec.auth_log[0].login_date).format('lll') : '-'
      }
    ],
    []
  );
  const handleSearchWithinUsers = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value) {
      // Limit input length to prevent potential DoS
      const searchValue = value.slice(0, 100).toLowerCase();

      /**
       * We have to use type 'any' to avoid TS2232 error
       */
      const baseList = showInactiveUsers ? UserOrig : UserOrig.filter((u) => !u.blocked);
      const filteredUsers: any = filter(baseList, (item: UserRecord) => {
        const matchedRoles = item.user_roles?.filter((role) => role.toLowerCase().includes(searchValue));
        const name = `${item.first_name || ''} ${item.last_name || ''}`.toLowerCase();
        return (
          (!isNil(name) && name.includes(searchValue)) ||
          (!isNil(item.email) && item.email.toLowerCase().includes(searchValue)) ||
          (!isNil(item.title) && item.title.toLowerCase().includes(searchValue)) ||
          (!isNil(item.client?.name) && item.client?.name.toLowerCase().includes(searchValue)) ||
          (matchedRoles && matchedRoles.length > 0)
        );
      });

      setUsers(filteredUsers);
    } else {
      const baseList = showInactiveUsers ? UserOrig : UserOrig.filter((u) => !u.blocked);
      setUsers(baseList);
    }
  };

  const handleShowInactiveUsers = (e: CheckboxChangeEvent) => {
    const { checked } = e.target;
    setShowInactiveUsers(checked);
    setUsers(checked ? UserOrig : UserOrig.filter((user) => !user.blocked));
  };
  return (
    <div className="users">
      <Row justify="space-between">
        <Col>
          <Typography.Title level={4}>Users</Typography.Title>
        </Col>
        <Col>
          <Space>
            <Button
              type="default"
              loading={loading}
              icon={<SyncOutlined spin={loading} />}
              onClick={handleClickRefresh}
            >
              Refresh
            </Button>
            <Button type="default" onClick={handleExport} icon={<DownloadOutlined />} loading={creatingExcel}>
              Download
            </Button>
          </Space>
        </Col>
      </Row>

      <Divider className="my-4" />
      <div style={{ margin: `12px 0 ${users.length > 0 ? '-40px' : 0}` }}>
        <Input
          placeholder="Filter by Name, Account, Email, Title, or Role"
          prefix={<SearchOutlined />}
          ref={filterInputRef}
          allowClear
          onChange={handleSearchWithinUsers}
          style={{ width: 410, marginRight: 10 }}
          size="large"
        />
        <Checkbox checked={showInactiveUsers} onChange={handleShowInactiveUsers}>
          Include Inactive Users
        </Checkbox>
      </div>
      <Table
        rowKey="user_id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={users}
        pagination={{
          ...DEFAULT_TABLE_PAGINATION_CONFIG,
          defaultPageSize: 50
        }}
      />
    </div>
  );
};

export default Users;
